import type { JWTPayload } from 'jose';

import {
  DateTime,
  Duration,
  Effect,
  Match,
  Option,
  Schema,
  SchemaGetter,
  SchemaIssue,
} from 'effect';
import { decodeJwt } from 'jose';

import { JwtDecodeError } from '../data/error/auth';

/**
 * Token status enumeration
 */
export const TokenStatus = {
  noToken: 'noToken',
  isCloseToExpiration: 'isCloseToExpiration',
  isTokenExpired: 'isTokenExpired',
  isRefreshTokenExpired: 'refreshTokenExpired',
  ok: 'ok',
} as const;

export type TokenStatusT = (typeof TokenStatus)[keyof typeof TokenStatus];

/**
 * Schema that transforms Unix timestamp (seconds) to DateTime.Utc
 * JWT timestamps are Unix epoch in seconds, need to be multiplied by 1000 for milliseconds
 */
const UnixTimestampToDateTimeUtc = Schema.Finite.pipe(
  Schema.decodeTo(Schema.DateTimeUtcFromDate, {
    decode: SchemaGetter.transform((seconds: number) => new Date(seconds * 1000)),
    encode: SchemaGetter.transform((date: Date) => Math.floor(date.getTime() / 1000)),
  })
);

/**
 * Schema that transforms Unix timestamp (seconds) to DateTime.Zoned in the supplied timezone
 * Combines UnixTimestampToDateTimeUtc with timezone conversion
 */
const UnixTimestampToDateTimeZoned = (timezone: string) =>
  UnixTimestampToDateTimeUtc.pipe(
    Schema.decodeTo(Schema.DateTimeZoned, {
      decode: SchemaGetter.transformEffect((utc) => {
        const zoneOption = DateTime.zoneFromString(timezone);

        if (Option.isNone(zoneOption)) {
          return Effect.fail(
            new SchemaIssue.InvalidValue({
              message: `Invalid timezone: ${timezone}`,
            })
          );
        }

        const zoned = DateTime.setZone(utc, zoneOption.value);
        return Effect.succeed(zoned);
      }),
      // v4: `DateTime.unsafeFromDate` → `DateTime.fromDateUnsafe`; `DateTime.toDate` → `DateTime.toDateUtc`.
      encode: SchemaGetter.transform((zoned) => DateTime.fromDateUnsafe(DateTime.toDateUtc(zoned))),
    })
  );

/**
 * Schema for JWT payload validation
 * Ensures all required fields are present and have correct types
 * JWT timestamps (exp, iat) are Unix timestamps in seconds, converted to DateTime.Zoned
 */
export const JwtPayloadSchema = (timezone: string) =>
  Schema.Struct({
    exp: UnixTimestampToDateTimeZoned(timezone),
    iat: UnixTimestampToDateTimeZoned(timezone),
    roles: Schema.Array(Schema.String),
    username: Schema.String,
  }).annotate({ identifier: 'JwtPayload' });

export type JwtPayloadT = Schema.Schema.Type<ReturnType<typeof JwtPayloadSchema>>;

/**
 * Decodes a JWT token string to raw payload
 */
function decodeJwtString(tokenStr: string) {
  return Effect.try({
    try: () => decodeJwt(tokenStr),
    catch: (error) =>
      new JwtDecodeError({
        token: tokenStr.substring(0, 20) + '...', // Only show the first 20 chars for security
        cause: error,
      }),
  });
}

/**
 * Validates raw JWT payload against schema
 * @param rawPayload - Raw decoded JWT payload
 * @returns Effect that succeeds with validated payload or fails with JwtDecodeError
 */
function validateJwtPayload(
  rawPayload: JWTPayload,
  timezone: string
): Effect.Effect<JwtPayloadT, JwtDecodeError> {
  return Schema.decodeUnknownEffect(JwtPayloadSchema(timezone))(rawPayload).pipe(
    Effect.mapError((error) => new JwtDecodeError({ cause: error }))
  );
}

/**
 * Effect-based JWT token decoder with schema validation
 * Decodes a JWT token and validates the payload structure
 * @param tokenStr - JWT token string
 * @param timezone - IANA timezone used for decoded timestamps
 * @returns Effect that succeeds with validated payload or fails with JwtDecodeError
 */
export function decodeTokenEffect(
  tokenStr: string,
  timezone: string
): Effect.Effect<JwtPayloadT, JwtDecodeError> {
  return decodeJwtString(tokenStr).pipe(
    Effect.flatMap((rawPayload) => validateJwtPayload(rawPayload, timezone))
  );
}

/**
 * Checks if a token is close to expiration using Effect DateTime
 * @param expirationDateTime - DateTime when token expires
 * @param currentDateTime - Current DateTime
 * @param window - Duration window to consider "close to expiration"
 * @returns true if token expires within the window
 */
export function isCloseToExpiration(
  expirationDateTime: DateTime.DateTime,
  currentDateTime: DateTime.DateTime,
  window: Duration.Duration
): boolean {
  const timeUntilExpiration = DateTime.distance(currentDateTime, expirationDateTime);
  return Duration.isLessThanOrEqualTo(timeUntilExpiration, window);
}

/**
 * Checks the status of a JWT token using Effect DateTime
 * @param token - Option containing the JWT token string
 * @param expirationWindow - Duration before expiration to consider token "close to expiring"
 * @param timezone - IANA timezone used for decoded token timestamps
 * @param refreshTokenExpiration - Optional refresh token expiration as Unix timestamp in seconds. When provided, checks refresh token first — if expired, returns isRefreshTokenExpired regardless of JWT status
 * @returns Effect that resolves to token status enum value (requires CurrentTimeZone service)
 */
export function checkTokenStatus(
  token: Option.Option<string>,
  expirationWindow: Duration.Duration,
  timezone: string,
  refreshTokenExpiration?: Option.Option<number>
): Effect.Effect<TokenStatusT, never, DateTime.CurrentTimeZone> {
  return Effect.gen(function* () {
    if (Option.isNone(token)) {
      return TokenStatus.noToken;
    }

    // Check refresh token first (like reference: if can't refresh, specific JWT status doesn't matter)
    if (refreshTokenExpiration !== undefined) {
      if (Option.isNone(refreshTokenExpiration)) {
        return TokenStatus.isRefreshTokenExpired;
      }
      const refreshExpired = yield* isRefreshTokenExpired(refreshTokenExpiration.value);
      if (refreshExpired) {
        return TokenStatus.isRefreshTokenExpired;
      }
    }

    const decodedTokenOption = yield* decodeTokenEffect(token.value, timezone).pipe(Effect.option);

    if (Option.isNone(decodedTokenOption)) {
      return TokenStatus.isTokenExpired;
    }

    const payload = decodedTokenOption.value;
    const currentDateTime = yield* DateTime.nowInCurrentZone;
    const expirationDateTime = payload.exp;

    if (DateTime.isGreaterThanOrEqualTo(currentDateTime, expirationDateTime)) {
      return TokenStatus.isTokenExpired;
    }

    if (isCloseToExpiration(expirationDateTime, currentDateTime, expirationWindow)) {
      return TokenStatus.isCloseToExpiration;
    }

    return TokenStatus.ok;
  });
}

/**
 * Checks if user is logged in based on token status
 * @param tokenStatus - Token status enum value
 * @returns true if user is considered logged in
 */
export function isLoggedIn(tokenStatus: TokenStatusT): boolean {
  return Match.value(tokenStatus).pipe(
    Match.when(TokenStatus.ok, () => true),
    Match.when(TokenStatus.isCloseToExpiration, () => true),
    Match.when(TokenStatus.isTokenExpired, () => false),
    Match.when(TokenStatus.noToken, () => false),
    Match.when(TokenStatus.isRefreshTokenExpired, () => false),
    Match.exhaustive
  );
}

/**
 * Checks if a refresh token is expired based on its Unix timestamp expiration
 * @param expirationTimestamp - Unix timestamp in seconds when refresh token expires
 * @returns Effect that resolves to true if refresh token is expired
 */
export function isRefreshTokenExpired(
  expirationTimestamp: number
): Effect.Effect<boolean, never, DateTime.CurrentTimeZone> {
  return Effect.gen(function* () {
    const now = yield* DateTime.nowInCurrentZone;
    const nowSeconds = Math.floor(DateTime.toEpochMillis(now) / 1000);
    return nowSeconds >= expirationTimestamp;
  });
}

/**
 * Checks if a JWT token is expired using Effect
 * Uses existing checkTokenStatus and isLoggedIn logic with DateTime.CurrentTimeZone
 * @param tokenStr - JWT token string
 * @param timezone - IANA timezone used for decoded token timestamps
 * @returns Effect that resolves to true if token is expired (user NOT logged in), false if valid (user logged in)
 */
export function checkIfTokenExpired(
  tokenStr: string,
  timezone: string
): Effect.Effect<boolean, never, DateTime.CurrentTimeZone> {
  return Effect.gen(function* () {
    const status = yield* checkTokenStatus(Option.some(tokenStr), Duration.minutes(0), timezone);
    return status === TokenStatus.isTokenExpired;
  });
}
